import { Dimensions, Position } from 'src/app/core/types';
import {
  Group,
  MeshBasicMaterial,
  Scene,
  Vector3,
  BoxGeometry,
  EdgesGeometry,
  MeshLambertMaterial,
  CylinderGeometry,
  DoubleSide,
  BufferGeometry,
  Object3D,
} from 'three';

import { LoadGroup } from './group';
import { Load } from './load';
import { LoadFactory } from './loadFactory';
import { LoadingSpace } from './loading-space';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { generateInterlayerStripeMaterials, generateTextMaterials } from './load-texture-generator';

export interface Covered {
  coveredDown: boolean;
  coveredLeft: boolean;
  coveredRight: boolean;
  coveredFront: boolean;
  coveredBack: boolean;
}

export const PALLET_CONTENT_OVERLAY_NAME = 'palletContentOverlay';

export function shouldRestoreLoadOpacityAfterDrag(object: {
  children?: { name?: string }[];
}): boolean {
  return !object.children?.some((child) => child.name === PALLET_CONTENT_OVERLAY_NAME);
}

export class LoadsGroupsDrawer {
  private loadsGroupPositions: { id: number; position: Position; dimensions: Dimensions }[] = [];

  constructor(private printMode: boolean, private loadingSpace: Partial<LoadingSpace>) {}

  public setLoadingSpace(loadingSpace: LoadingSpace) {
    this.loadingSpace = loadingSpace;
  }

  public drawGroups(
    loadsGroups: LoadGroup[],
    scene: Scene,
    units: string,
    isShadows?: boolean,
    isPreview?: boolean,
  ): any[] {
    let group: Group;
    if (LoadFactory.size() && scene.getObjectByName('loadsGroup')) {
      group = scene.getObjectByName('loadsGroup') as Group;
    } else {
      scene.remove(scene.getObjectByName('loadsGroup'));
      group = new Group();
      group.name = 'loadsGroup';
      scene.add(group);
    }
    this.loadsGroupPositions = [];
    loadsGroups.forEach((grp) => {
      const groupBeforePosition: { id: number; position: Position; dimensions: Dimensions } =
        this.loadsGroupPositions.find((g) => g.id === grp.id);
      if (groupBeforePosition) {
        groupBeforePosition.position.x =
          groupBeforePosition.position.x < grp.position.x
            ? groupBeforePosition.position.x
            : grp.position.x;

        groupBeforePosition.position.y =
          groupBeforePosition.position.y < grp.position.y
            ? groupBeforePosition.position.y
            : grp.position.y;

        groupBeforePosition.position.z =
          groupBeforePosition.position.z < grp.position.z
            ? groupBeforePosition.position.z
            : grp.position.z;

        groupBeforePosition.dimensions.length =
          groupBeforePosition.dimensions.length >
          grp.dimensions.length * grp.loadsPerAxis.z +
            grp.position.z -
            groupBeforePosition.position.z
            ? groupBeforePosition.dimensions.length
            : grp.dimensions.length * grp.loadsPerAxis.z +
              grp.position.z -
              groupBeforePosition.position.z;

        groupBeforePosition.dimensions.width =
          groupBeforePosition.dimensions.width >
          grp.dimensions.width * grp.loadsPerAxis.x +
            grp.position.x -
            groupBeforePosition.position.x
            ? groupBeforePosition.dimensions.width
            : grp.dimensions.width * grp.loadsPerAxis.x +
              grp.position.x -
              groupBeforePosition.position.x;

        groupBeforePosition.dimensions.height =
          groupBeforePosition.dimensions.height >
          grp.dimensions.height * grp.loadsPerAxis.y +
            grp.position.y -
            groupBeforePosition.position.y
            ? groupBeforePosition.dimensions.height
            : grp.dimensions.height * grp.loadsPerAxis.y +
              grp.position.y -
              groupBeforePosition.position.y;

        groupBeforePosition.dimensions.diameter =
          groupBeforePosition.dimensions.diameter >
          grp.dimensions.diameter * grp.loadsPerAxis.z +
            grp.position.z -
            groupBeforePosition.position.z
            ? groupBeforePosition.dimensions.diameter
            : grp.dimensions.diameter * grp.loadsPerAxis.z +
              grp.position.z -
              groupBeforePosition.position.z;
      } else {
        this.loadsGroupPositions.push({
          id: grp.id,
          position: { ...grp.position },
          dimensions: {
            length: grp.dimensions.length * grp.loadsPerAxis.z,
            width: grp.dimensions.width * grp.loadsPerAxis.x,
            height: grp.dimensions.height * grp.loadsPerAxis.y,
            diameter: grp.dimensions.diameter * grp.loadsPerAxis.z,
          },
        });
      }
    });

    loadsGroups.forEach((loadsGroup) => {
      if (this.printMode && !this.loadingSpace.hasPosition(loadsGroup.position as Vector3)) {
        return;
      }
      this.drawGroup(loadsGroup, group, units, isShadows, isPreview, this.printMode);
    });
    return group.children.filter((child) => child.name !== PALLET_CONTENT_OVERLAY_NAME);
  }

  private drawGroup(
    loadsGroup: LoadGroup,
    group: Object3D,
    units: string,
    isShadows?: boolean,
    isPreview?: boolean,
    isPrint?: boolean,
    asOverlay = false,
  ) {
    let loadGeometry: BufferGeometry;
    const isInterlayer = loadsGroup.stackingOptions?.interlayer === true;
    if (isInterlayer) {
      loadGeometry = new BoxGeometry(
        loadsGroup.dimensions.width,
        loadsGroup.dimensions.height,
        loadsGroup.dimensions.length,
      ).translate(
        loadsGroup.dimensions.width / 2,
        loadsGroup.dimensions.height / 2,
        loadsGroup.dimensions.length / 2,
      );
    } else if (loadsGroup.loadType === 1) {
      loadGeometry = new CylinderGeometry(
        loadsGroup.dimensions.diameter / 2,
        loadsGroup.dimensions.diameter / 2,
        loadsGroup.dimensions.height,
        32,
      ).translate(
        loadsGroup.dimensions.diameter / 2,
        loadsGroup.dimensions.height / 2,
        loadsGroup.dimensions.diameter / 2,
      );
    } else if (loadsGroup.loadType === 2) {
      const isPipeLength = loadsGroup.dimensions.length >= loadsGroup.dimensions.width;
      const pipeLength = isPipeLength ? loadsGroup.dimensions.length : loadsGroup.dimensions.width;
      loadGeometry = new CylinderGeometry(
        loadsGroup.dimensions.diameter / 2,
        loadsGroup.dimensions.diameter / 2,
        pipeLength,
        32,
        1,
        true,
      )
        .translate(
          isPipeLength ? loadsGroup.dimensions.diameter / 2 : -loadsGroup.dimensions.diameter / 2,
          pipeLength / 2,
          -loadsGroup.dimensions.diameter / 2,
        )
        .rotateX(Math.PI / 2);
      if (!isPipeLength) {
        loadGeometry.rotateY(Math.PI / 2);
      }
    } else {
      loadGeometry = new BoxGeometry(
        loadsGroup.dimensions.width,
        loadsGroup.dimensions.height,
        loadsGroup.dimensions.length,
      ).translate(
        loadsGroup.dimensions.width / 2,
        loadsGroup.dimensions.height / 2,
        loadsGroup.dimensions.length / 2,
      );
    }
    const edgesGeometry = new EdgesGeometry(loadGeometry);
    const edges = new LineSegmentsGeometry().fromEdgesGeometry(edgesGeometry);
    let meshMaterial: MeshBasicMaterial[] | MeshLambertMaterial[] = undefined;
    let meshText: MeshBasicMaterial[] | MeshLambertMaterial[] = undefined;
    const showLoadName = loadsGroup.showLoadName;
    if (isInterlayer) {
      meshMaterial = generateInterlayerStripeMaterials(loadsGroup.color, isShadows);
    } else if (showLoadName) {
      const labelDimensions = dimensionsForLoadLabel(loadsGroup);
      const loadWidth: number = loadsGroup.loadType === 1 || loadsGroup.loadType === 2
        ? loadsGroup.loadType === 2 ? labelDimensions.length ? labelDimensions.length : labelDimensions.width: labelDimensions.diameter * 2
        : labelDimensions.width;
      const loadHeight: number = loadsGroup.loadType === 2 ? labelDimensions.diameter * 2: labelDimensions.height;
      const loadLength: number = loadsGroup.loadType === 1 || loadsGroup.loadType === 2
        ?  loadsGroup.loadType === 2 ? labelDimensions.length ? labelDimensions.length : labelDimensions.width: labelDimensions.diameter * 2
        : labelDimensions.length;
      meshText = generateTextMaterials(
        loadWidth,
        loadHeight,
        loadLength,
        units,
        loadsGroup.color,
        loadsGroup.nameTranslated,
        false,
        isShadows,
        loadsGroup.centerOfMass,
        isPreview,
        loadsGroup.loadType,
        document
      );
    } else {
      meshMaterial = isShadows
        ? [
            new MeshLambertMaterial({
              color: loadsGroup.color,
              side: DoubleSide,
            }),
          ]
        : [
            new MeshBasicMaterial({
              color: loadsGroup.color,
              side: DoubleSide,
            }),
          ];
    }
    for (let i = 0; i < loadsGroup.loadsPerAxis.z; i++) {
      for (let j = 0; j < loadsGroup.loadsPerAxis.x; j++) {
        for (let k = 0; k < loadsGroup.loadsPerAxis.y; k++) {
          const stacked: boolean =
            loadsGroup.loadsPerAxis.y > 1 && k + 1 < loadsGroup.loadsPerAxis.y;
          const coveredDown: boolean = loadsGroup.loadsPerAxis.y > 1 && k !== 0;
          const coveredTop: boolean =
            loadsGroup.loadsPerAxis.y > 1 && k + 1 < loadsGroup.loadsPerAxis.y;

          const coveredFront: boolean =
            loadsGroup.loadsPerAxis.x > 1 && j + 1 < loadsGroup.loadsPerAxis.x;
          const coveredBack: boolean = loadsGroup.loadsPerAxis.x > 1 && j !== 0;

          const coveredRight: boolean =
            loadsGroup.loadsPerAxis.z > 1 && i + 1 < loadsGroup.loadsPerAxis.z;
          const coveredLeft: boolean = loadsGroup.loadsPerAxis.z > 1 && i !== 0;

          const options = {
            position: {
              x: loadsGroup.position.x + loadsGroup.dimensions.width * j,
              y: loadsGroup.position.y + loadsGroup.dimensions.height * k,
              z: loadsGroup.position.z + loadsGroup.dimensions.length * i,
            },
            stacked,
            covered: {
              coveredDown,
              coveredFront,
              coveredBack,
              coveredRight,
              coveredLeft,
            },
            units,
            pctSupport: loadsGroup.pctSupport,
            stackingOptions: loadsGroup.stackingOptions,
          };
          const material = showLoadName ? meshText : meshMaterial;
          let load: Load = asOverlay
            ? LoadFactory.create(
                loadsGroup,
                options,
                material,
                loadGeometry,
                edges,
                isShadows,
                isPreview,
                isPrint,
              )
            : LoadFactory.get(
                loadsGroup,
                options,
                material,
                loadGeometry,
                edges,
                undefined,
                isPreview,
                isPrint,
              );

          if (load) {
            if (!asOverlay && loadsGroup.palletContentLoadGroups?.length) {
              this.hidePalletShell(load);
              this.removePalletContentOverlays(load);
              loadsGroup.palletContentLoadGroups.forEach((contentGroup) => {
                this.drawGroup(
                  contentGroup,
                  load,
                  units,
                  isShadows,
                  isPreview,
                  isPrint,
                  true,
                );
              });
            } else if (!asOverlay) {
              this.removePalletContentOverlays(load);
              this.showPalletShell(load);
            }
            if (this.hasLoadRotation(load)) {
              try {
                load = this.rotateLoad(load) as Load;
              } catch (e) {
                // TODO: acesarski during pdf generation it started throwing error with types look at this
                console.error(load, e);
              }
            }
            if (asOverlay) {
              load.name = PALLET_CONTENT_OVERLAY_NAME;
              this.disableRaycast(load);
            }
            const isCovered: boolean =
              coveredTop &&
              coveredDown &&
              coveredBack &&
              coveredFront &&
              coveredLeft &&
              coveredRight;
            const isCube: boolean = load.loadType === 0;
            if (!isCube || !isCovered) {
              if (!asOverlay) {
                LoadFactory.set(options.position, load);
              }
              group.add(load);
            }
          }
        }
      }
    }
  }

  private hasLoadRotation(load: Load): boolean {
    return (load.yRotation ?? 0) !== 0 || (load.edgeRotation ?? 0) !== 0;
  }

  private rotateLoad(load: Load): Load | Object3D {
    const isEdgeRotation = (load.edgeRotation ?? 0) !== 0;
    const isYrotation = (load.yRotation ?? 0) !== 0;
    if (isEdgeRotation && !isYrotation) {
      return this.rotateX(load);
    }
    if (isYrotation && !isEdgeRotation) {
      return this.rotateY(load);
    }
    if (isYrotation && isEdgeRotation) {
      return this.rotateYX(load);
    }
    return load;
  }

  private hidePalletShell(load: Load): void {
    const materials = Array.isArray(load.material) ? load.material : [load.material];
    materials.forEach((material) => {
      if (!material) {
        return;
      }
      material.transparent = true;
      material.opacity = 0;
      material.depthWrite = false;
    });
    load.children.forEach((child) => {
      if (child.name === 'LineSegment') {
        child.visible = true;
      }
    });
  }

  private showPalletShell(load: Load): void {
    const materials = Array.isArray(load.material) ? load.material : [load.material];
    materials.forEach((material) => {
      if (!material) {
        return;
      }
      material.transparent = false;
      material.opacity = 1;
      material.depthWrite = true;
    });
    load.children.forEach((child) => {
      if (child.name === 'LineSegment') {
        child.visible = true;
      }
    });
  }

  private removePalletContentOverlays(parent: Object3D): void {
    parent.children
      .filter((child) => child.name === PALLET_CONTENT_OVERLAY_NAME)
      .forEach((child) => parent.remove(child));
  }

  private disableRaycast(object: Object3D): void {
    object.traverse((child) => {
      child.raycast = () => undefined;
    });
  }

  private rotateX(load: Load): Load | Object3D {
    const container = new Object3D();
    container.name = `container_${load.name}`;
    const pivot = new Object3D();
    pivot.name = `pivot_${load.name}`;
    const objectClone = load;
    pivot.add(objectClone);
    container.add(pivot);
    pivot.position.set(load.position.x + load.width / 2, load.position.y, load.position.z);
    objectClone.position.set(-load.width / 2, 0, 0);
    objectClone.rotation.x = load.edgeRotation / 100;
    load.rotationDifference = new Vector3(
      objectClone.oldPosition.x - container.position.x,
      objectClone.oldPosition.y - container.position.y,
      objectClone.oldPosition.z - container.position.z,
    );
    return container;
  }

  private rotateY(load: Load): Load | Object3D {
    const container = new Object3D();
    container.name = `container_${load.name}`;
    const pivot = new Object3D();
    pivot.name = `pivot_${load.name}`;
    const objectClone = load;
    pivot.add(objectClone);
    pivot.rotation.set(0, objectClone.yRotation / 100, 0);
    container.add(pivot);
    pivot.position.set(
      load.position.x + load.width / 2,
      load.position.y,
      load.position.z + load.length / 2,
    );
    objectClone.position.set(-objectClone.width / 2, 0, -objectClone.length / 2);
    load.rotationDifference = new Vector3(
      objectClone.oldPosition.x - pivot.position.x,
      objectClone.oldPosition.y - pivot.position.y,
      objectClone.oldPosition.z - pivot.position.z,
    );
    return container;
  }

  private rotateYX(load: Load): Object3D {
    const container = new Object3D();
    container.name = `container_${load.name}`;
    const pivot = new Object3D();
    pivot.name = `pivot_${load.name}`;
    const objectClone = load;
    pivot.add(objectClone);
    container.add(pivot);
    pivot.position.set(load.position.x + load.width / 2, load.position.y, load.position.z);
    pivot.rotation.y = load.yRotation / 100;
    objectClone.position.set(-load.width / 2, 0, 0);
    objectClone.rotation.x = load.edgeRotation / 100;
    load.rotationDifference = new Vector3(
      objectClone.oldPosition.x - container.position.x,
      objectClone.oldPosition.y - container.position.y,
      objectClone.oldPosition.z - container.position.z,
    );
    return container;
  }
}

function dimensionsForLoadLabel(loadsGroup: LoadGroup): Dimensions {
  const dims = loadsGroup.dimensions;
  const base = loadsGroup.baseDimensions;
  if (!base || (base.height ?? 0) <= (dims.height ?? 0) + 0.01) {
    return dims;
  }
  return {
    width: dims.width ?? base.width,
    length: dims.length ?? base.length,
    height: base.height,
    diameter: dims.diameter ?? base.diameter,
  };
}
